import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, Container, Typography, Button, Dialog, DialogContent, 
  DialogTitle, DialogActions, Box, Grid, IconButton, Chip, TextField, MenuItem,
  useMediaQuery, Drawer, FormControl, Select, InputLabel, Modal, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Menu,
  Divider, Accordion, AccordionSummary, AccordionDetails, CircularProgress, Autocomplete
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { 
  ArrowLeft, ChevronDown, ChevronUp, Mic, CheckSquare, Calendar, Search, Phone, Upload as UploadIcon, Sparkles,
  Upload, Building, Globe, Users, FileText, Video, Play, Plus, Edit, Share2, RefreshCw
} from 'lucide-react';
import { 
  Business, Language, People, Description, Videocam, PlayArrow, Add, Edit as EditMui, ExpandMore, 
  ExpandLess, CheckBox, CalendarToday, CloudUpload, AutoAwesome, Replay10, Forward10, Pause, Close as CloseIcon,
  Edit as EditIcon, Check as CheckIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon,
  School as SchoolIcon, Info as InfoIcon, GetApp as DownloadIcon, Cancel as CancelIcon, VideoCall as VideoCallIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import apiInstance from 'src/utils/apiAuth';
import * as constants from 'src/utils/constants';
import UserInfo from 'src/utils/Authorization/UserInfo';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import StageTaskManagement from '../tasks/StageTaskManagement';
import AddCompany from '../company/AddCompany';
import { tasksApi } from 'src/utils/tasksApi';
import MediaViewer from 'src/components/MediaViewer';
import UDocViewer from 'src/components/MediaViewer/UDocViewer';
import React from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import { createVideoThumbnail } from 'src/utils/videoThumbnailUtil';

async function updateDocumentFileName(documentId, originalName, editedBaseName) {
  const fileExtension = originalName && originalName.includes('.')
    ? originalName.substring(originalName.lastIndexOf('.'))
    : '';
  const newFileName = `${editedBaseName}${fileExtension}`;

  const response = await apiInstance.put('/company-documents/update-file-name/', {
    file_name: newFileName,
    document_id: documentId
  }, {
    headers: {
      Authorization: `Token ${localStorage.getItem('authToken')}`
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to update file name');
  }

  return newFileName;
}

// ============================================
// SHARED DRAWER COMPONENTS
// ============================================

// Shared Add to Learning Drawer - used by all card types
function AddToLearningDrawer({ open, onClose, documentId, documentTitle, companyId, stageId, onSuccess }) {
  const [learningNotes, setLearningNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      setLearningNotes('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!learningNotes.trim()) {
      showErrorToast('Please add notes explaining why this video is important');
      return;
    }
    if (!documentId) return;
    setIsSaving(true);
    try {
      const payload = { document_id: documentId, notes: learningNotes };
      if (companyId) payload.company_id = companyId;
      if (stageId) payload.stage_id = stageId;
      const response = await apiInstance.post('learning/', payload, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      if (response.data?.success) {
        showToast('Video added to Learning successfully', 'success');
        onClose();
        setLearningNotes('');
        if (onSuccess) onSuccess(response.data.data);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.errors?.document_id?.[0] ||
                       error.response?.data?.message ||
                       'Failed to add video to Learning';
      showErrorToast(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{ sx: { width: '400px', maxWidth: '90vw' } }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: '#1976d2', color: 'white', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon />
          <Typography variant="h6">Add to Learning</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      {/* Content */}
      <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">Adding</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{documentTitle || 'Video'}</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Learning Notes</Typography>
          <TextField
            autoFocus
            multiline
            rows={5}
            fullWidth
            placeholder={"Explain why this video is important for learning. Include:\n• Key takeaways or lessons\n• Important timestamps (e.g., '5:30 - negotiation technique')\n• Who should watch this and why"}
            value={learningNotes}
            onChange={(e) => setLearningNotes(e.target.value)}
            InputProps={{ sx: { fontSize: '0.9rem' } }}
          />
        </Box>
        <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> This video will be visible to all team members in the Learning section,
            grouped by its original stage. Your notes will help others understand why this video is valuable.
          </Typography>
        </Box>
      </Box>
      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton onClick={handleSave} variant="contained" loading={isSaving} startIcon={<SchoolIcon />}>
          Add to Learning
        </LoadingButton>
      </Box>
    </Drawer>
  );
}

// Shared Share Video Drawer - used by all card types
function ShareVideoDrawer({ open, onClose, documentId, documentTitle, companyId, onShareSuccess, onRemoveSuccess }) {
  const [shareExpiryDays, setShareExpiryDays] = useState(30);
  const [selectedInternalUsers, setSelectedInternalUsers] = useState([]);
  const [selectedExternalMembers, setSelectedExternalMembers] = useState([]);
  const [shareNotes, setShareNotes] = useState('');
  const [isSavingShare, setIsSavingShare] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sendShareEmails, setSendShareEmails] = useState(true);
  const [isShared, setIsShared] = useState(false);
  const [sharedData, setSharedData] = useState(null);
  const [isRemovingShare, setIsRemovingShare] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const expiryOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90];

  // Fetch users and check shared status when drawer opens
  useEffect(() => {
    if (!open) return;
    // Reset form
    setSelectedInternalUsers([]);
    setSelectedExternalMembers([]);
    setAdditionalEmails([]);
    setShareNotes('');
    setShareExpiryDays(30);
    setSendShareEmails(true);
    setIsShared(false);
    setSharedData(null);

    const fetchData = async () => {
      setLoadingUsers(true);
      // Fetch internal users
      try {
        const usersRes = await apiInstance.get('users-viewset/', {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
          timeout: 10000
        });
        const usersData = usersRes.data?.data || usersRes.data?.results || usersRes.data || [];
        setInternalUsers(Array.isArray(usersData) ? usersData : []);
      } catch (err) {
        console.error('Error fetching internal users:', err);
        setInternalUsers([]);
      }
      // Fetch company members
      if (companyId) {
        try {
          const companyRes = await apiInstance.get(`company/${companyId}/`, {
            headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
            timeout: 10000
          });
          const membersData = companyRes.data?.data?.company_members || companyRes.data?.company_members || [];
          setCompanyMembers(Array.isArray(membersData) ? membersData : []);
        } catch (err) {
          console.error('Error fetching company members:', err);
          setCompanyMembers([]);
        }
      }
      // Check existing share
      if (documentId) {
        try {
          const response = await apiInstance.get(`shared-videos/check/${documentId}/`, {
            headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
          });
          if (response.data?.is_shared) {
            setIsShared(true);
            setSharedData(response.data.data);
          }
        } catch (error) {
          console.error('Error checking shared status:', error);
        }
      }
      setLoadingUsers(false);
    };
    fetchData();
  }, [open, documentId, companyId]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) { setEmailError('Please enter an email address'); return; }
    if (!validateEmail(trimmedEmail)) { setEmailError('Please enter a valid email address'); return; }
    if (additionalEmails.includes(trimmedEmail)) { setEmailError('Email already added'); return; }
    setAdditionalEmails([...additionalEmails, trimmedEmail]);
    setEmailInput('');
    setEmailError('');
  };

  const handleDeleteEmail = (emailToDelete) => {
    setAdditionalEmails(additionalEmails.filter(email => email !== emailToDelete));
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); }
  };

  const handleSaveShare = async () => {
    if (selectedInternalUsers.length === 0 && selectedExternalMembers.length === 0 && additionalEmails.length === 0) {
      showErrorToast('Please select at least one recipient or add an email');
      return;
    }
    if (!documentId) return;
    setIsSavingShare(true);
    try {
      const requestData = {
        document_id: documentId,
        company_id: parseInt(companyId),
        internal_user_ids: selectedInternalUsers.map(u => u.pk),
        external_member_ids: selectedExternalMembers.map(m => m.id),
        additional_emails: additionalEmails,
        expiry_days: parseInt(shareExpiryDays),
        notes: shareNotes || '',
        send_emails: sendShareEmails
      };
      const response = await apiInstance.post('shared-videos/', requestData, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      if (response.data?.success) {
        showToast('Video shared successfully', 'success');
        setIsShared(true);
        setSharedData(response.data.data);
        setSelectedInternalUsers([]);
        setSelectedExternalMembers([]);
        setAdditionalEmails([]);
        setShareNotes('');
        setShareExpiryDays(30);
        if (onShareSuccess) onShareSuccess(response.data.data);
      }
    } catch (error) {
      console.error('Share video error response:', error.response?.data);
      const errorData = error.response?.data;
      const errorMsg = errorData?.errors?.document_id?.[0] ||
                       errorData?.errors?.expiry_days?.[0] ||
                       errorData?.errors?.non_field_errors?.[0] ||
                       errorData?.message ||
                       JSON.stringify(errorData?.errors) ||
                       'Failed to share video';
      showErrorToast(errorMsg);
    } finally {
      setIsSavingShare(false);
    }
  };

  const handleRemoveShare = async () => {
    if (!sharedData?.id) return;
    setIsRemovingShare(true);
    try {
      const response = await apiInstance.delete(`shared-videos/${sharedData.id}/`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      if (response.data?.success) {
        showToast('Share access removed successfully', 'success');
        setIsShared(false);
        setSharedData(null);
        onClose();
        if (onRemoveSuccess) onRemoveSuccess();
      }
    } catch (error) {
      console.error('Remove share error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to remove share access');
    } finally {
      setIsRemovingShare(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 450 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Share2 size={24} color="#2e7d32" />
            Share Video
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {documentTitle || 'Video'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This video will be shared with selected recipients
          </Typography>
        </Box>

        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Internal Users */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Internal Users (Team Members)
              </Typography>
              <Autocomplete
                multiple
                options={internalUsers}
                value={selectedInternalUsers}
                onChange={(e, newValue) => setSelectedInternalUsers(newValue)}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                isOptionEqualToValue={(option, value) => option.pk === value.pk}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select team members" size="small" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.pk} label={`${option.first_name} ${option.last_name}`} size="small" />
                  ))
                }
              />
            </Box>

            {/* External Members */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                External Members (Company Contacts)
              </Typography>
              <Autocomplete
                multiple
                options={companyMembers}
                value={selectedExternalMembers}
                onChange={(e, newValue) => setSelectedExternalMembers(newValue)}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name || ''} ${option.email ? `(${option.email})` : ''}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select company contacts" size="small" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={`${option.first_name} ${option.last_name || ''}`} size="small" />
                  ))
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                External members will receive a unique link and access code
              </Typography>
            </Box>

            {/* Add New External Email */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Add External Recipients (Not in System)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter email address"
                  value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                  onKeyPress={handleEmailKeyPress}
                  error={Boolean(emailError)}
                  helperText={emailError}
                />
                <Button variant="outlined" onClick={handleAddEmail} startIcon={<Add size={16} />} sx={{ minWidth: '80px', height: '40px' }}>
                  Add
                </Button>
              </Box>
              {additionalEmails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                  {additionalEmails.map((email, index) => (
                    <Chip key={index} label={email} onDelete={() => handleDeleteEmail(email)} size="small"
                      sx={{ bgcolor: '#e3f2fd', '& .MuiChip-deleteIcon': { color: '#1976d2' } }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                These recipients will receive an email with access link and code
              </Typography>
            </Box>

            {/* Expiry Days */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Access Expires In</Typography>
              <FormControl fullWidth size="small">
                <Select value={shareExpiryDays} onChange={(e) => setShareExpiryDays(e.target.value)}>
                  {expiryOptions.map((days) => (
                    <MenuItem key={days} value={days}>{days} days</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Notes */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Notes (Optional)</Typography>
              <TextField multiline rows={3} fullWidth size="small" placeholder="Add a message for recipients..."
                value={shareNotes} onChange={(e) => setShareNotes(e.target.value)}
              />
            </Box>

            {/* Send Email Checkbox */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <input type="checkbox" id={`sendEmails-${documentId}`} checked={sendShareEmails}
                onChange={(e) => setSendShareEmails(e.target.checked)} style={{ marginRight: 8 }}
              />
              <label htmlFor={`sendEmails-${documentId}`}>
                <Typography variant="body2">Send email notifications to recipients</Typography>
              </label>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: isShared ? 3 : 0 }}>
              <Button onClick={onClose}>Cancel</Button>
              <LoadingButton variant="contained" onClick={handleSaveShare} loading={isSavingShare}
                startIcon={<Share2 size={18} />} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
              >
                Share Video
              </LoadingButton>
            </Box>

            {/* Previously Shared Info */}
            {isShared && sharedData && (
              <Box sx={{ mt: 2, pt: 3, borderTop: '2px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.1rem' }}>📋</span> Previously Shared
                  </Typography>
                  <Button size="small" variant="outlined"
                    onClick={() => {
                      const textToCopy = `Share URL: ${sharedData.public_url || 'N/A'}\nAccess Code: ${sharedData.access_code || 'N/A'}`;
                      navigator.clipboard.writeText(textToCopy);
                      showToast('URL and Access Code copied to clipboard', 'success');
                    }}
                    startIcon={<span style={{ fontSize: '0.9rem' }}>📋</span>}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: '#2e7d32', color: '#2e7d32',
                      '&:hover': { borderColor: '#1b5e20', bgcolor: 'rgba(46, 125, 50, 0.04)' }
                    }}
                  >
                    Copy All
                  </Button>
                </Box>

                {/* Share URL */}
                {sharedData.public_url && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Share URL</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {sharedData.public_url}
                      </Typography>
                      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(sharedData.public_url); showToast('URL copied', 'success'); }} sx={{ flexShrink: 0 }}>
                        <span style={{ fontSize: '1rem' }}>📋</span>
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Access Code */}
                {sharedData.access_code && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Access Code</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                      <Typography variant="body1" sx={{ flex: 1, fontWeight: 700, fontSize: '1.1rem', fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                        {sharedData.access_code}
                      </Typography>
                      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(sharedData.access_code); showToast('Access code copied', 'success'); }} sx={{ flexShrink: 0 }}>
                        <span style={{ fontSize: '1rem' }}>📋</span>
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Shared With Info */}
                {(sharedData.internal_users?.length > 0 || sharedData.external_members?.length > 0 || sharedData.additional_emails?.length > 0) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Shared With</Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {sharedData.internal_users?.map((user, idx) => (
                        <Chip key={`user-${idx}`} label={`${user.first_name} ${user.last_name || ''}`.trim() || user.email || 'User'} size="small" sx={{ bgcolor: '#e3f2fd' }} />
                      ))}
                      {sharedData.external_members?.map((member, idx) => (
                        <Chip key={`member-${idx}`} label={`${member.first_name} ${member.last_name || ''}`.trim() || member.email || 'Contact'} size="small" sx={{ bgcolor: '#fff3e0' }} />
                      ))}
                      {sharedData.additional_emails?.map((email, idx) => (
                        <Chip key={`email-${idx}`} label={email} size="small" sx={{ bgcolor: '#fce4ec' }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Expiry Info */}
                {sharedData.expires_at && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Expires On</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {new Date(sharedData.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                )}

                {/* Remove Access Button */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <LoadingButton fullWidth variant="outlined" color="error" onClick={handleRemoveShare}
                    loading={isRemovingShare} startIcon={<DeleteIcon />} sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Remove Access
                  </LoadingButton>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                    This will revoke public access to this video
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}

// Teams Recording Card - shows recording with inline video player (no nested dialog)
function TeamsRecordingCard({ recording, meetingId, onRecordingFetched, companyId, stageId }) {
  const [videoError, setVideoError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [retrySuccess, setRetrySuccess] = useState(false);
  const [currentRecording, setCurrentRecording] = useState(recording);
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Learning states (lightweight - drawer handled by shared component)
  const [learningDialogOpen, setLearningDialogOpen] = useState(false);
  const [isInLearning, setIsInLearning] = useState(false);
  
  // Share states (lightweight - drawer handled by shared component)
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [isShared, setIsShared] = useState(false);
  
  const handleMenuClick = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleShareClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    setShareDrawerOpen(true);
  };
  
  const handleLearningClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    setLearningDialogOpen(true);
  };
  
  // Build the file URL - ensure it starts with /media/ if it's a relative path
  let fileUrl = null;
  if (currentRecording?.file) {
    if (currentRecording.file.startsWith('http')) {
      fileUrl = currentRecording.file;
    } else {
      // Ensure the path starts with /media/ for proper serving
      const filePath = currentRecording.file.startsWith('/') ? currentRecording.file : `/${currentRecording.file}`;
      fileUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${filePath}`;
    }
  }

  // Retry fetching the recording from Microsoft Teams
  const handleRetryFetch = async () => {
    if (!meetingId || isRetrying) return;
    
    setIsRetrying(true);
    setRetryError(null);
    setRetrySuccess(false);
    
    try {
      console.log('Fetching Teams recording for meeting:', meetingId);
      const response = await apiInstance.post(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/teams-recording-fetch/`,
        { meeting_id: meetingId, force_refetch: true },
        { headers: { 'Authorization': `Token ${localStorage.getItem('authToken')}` } }
      );
      console.log('Teams recording fetch response:', response.data);
      
      if (response.status === 200 && response.data?.success && response.data?.recording) {
        setCurrentRecording(response.data.recording);
        setVideoError(false);
        setRetrySuccess(true);
        if (onRecordingFetched) {
          onRecordingFetched(response.data.recording);
        }
        showToast('Recording fetched successfully!', 'success');
      } else if (response.data?.message) {
        setRetryError(response.data.message);
        showErrorToast(response.data.message);
      } else {
        setRetryError('Failed to fetch recording');
        showErrorToast('Failed to fetch recording');
      }
    } catch (error) {
      console.error('Error re-fetching Teams recording:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch recording';
      setRetryError(errorMsg);
      showErrorToast(errorMsg);
    } finally {
      setIsRetrying(false);
    }
  };

  if (!fileUrl) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Meeting Recording
      </Typography>
      <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', backgroundColor: '#000', position: 'relative' }}>
        {/* 3-dot menu at top-left like regular video cards */}
        {currentRecording?.id && (
          <IconButton 
            size="small" 
            onClick={handleMenuClick}
            sx={{ 
              position: 'absolute', 
              top: 8, 
              left: 8, 
              zIndex: 3, 
              backgroundColor: 'rgba(0,0,0,0.6)', 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
        <Chip label="REC" size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, backgroundColor: '#f44336', color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }} />
        {videoError ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'white' }}>
            <Typography variant="body2">Video unavailable</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mb: 1 }}>Recording may still be processing</Typography>
            {retryError && (
              <Typography variant="caption" sx={{ color: '#ff6b6b', display: 'block', mb: 1 }}>{retryError}</Typography>
            )}
            {retrySuccess && (
              <Typography variant="caption" sx={{ color: '#69db7c', display: 'block', mb: 1 }}>Recording fetched! Reloading...</Typography>
            )}
            {meetingId ? (
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetryFetch();
                }}
                disabled={isRetrying || retrySuccess}
                sx={{ mt: 1, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              >
                {isRetrying ? <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} /> : null}
                {isRetrying ? 'Fetching from Microsoft...' : 'Retry Fetch Recording'}
              </Button>
            ) : (
              <Typography variant="caption" sx={{ color: '#ff6b6b', display: 'block' }}>Meeting ID not available</Typography>
            )}
          </Box>
        ) : (
          <video
            src={fileUrl}
            controls
            controlsList="nodownload"
            style={{ width: '100%', maxHeight: '320px', display: 'block', objectFit: 'contain' }}
            preload="metadata"
            onError={() => setVideoError(true)}
          />
        )}
      </Box>
      <Box sx={{ pt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }} noWrap>
          {currentRecording.file_name || 'Teams Meeting Recording'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {currentRecording.created ? new Date(currentRecording.created).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
        </Typography>
      </Box>
      
      {/* 3-dot Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleLearningClick} disabled={isInLearning}>
          <SchoolIcon sx={{ fontSize: 16, mr: 1 }} /> {isInLearning ? 'In Learning' : 'Add to Learning'}
        </MenuItem>
        <MenuItem onClick={handleShareClick}>
          <Share2 size={16} style={{ marginRight: 8 }} /> Share Video
        </MenuItem>
      </Menu>
      
      {/* Add to Learning Drawer - uses shared component */}
      <AddToLearningDrawer
        open={learningDialogOpen}
        onClose={() => setLearningDialogOpen(false)}
        documentId={currentRecording?.id}
        documentTitle={currentRecording?.file_name || 'Teams Recording'}
        companyId={companyId}
        stageId={stageId}
        onSuccess={(data) => setIsInLearning(true)}
      />

      {/* Share Video Drawer - uses shared component */}
      <ShareVideoDrawer
        open={shareDrawerOpen}
        onClose={() => setShareDrawerOpen(false)}
        documentId={currentRecording?.id}
        documentTitle={currentRecording?.file_name || 'Teams Recording'}
        companyId={companyId}
        onShareSuccess={(data) => setIsShared(true)}
        onRemoveSuccess={() => setIsShared(false)}
      />
    </Box>
  );
}

// Top Drop Zone Component - separate droppable for the top area
function TopDropZone({ stageId, stageName, isDragging }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `top-drop-${stageId}`,
    data: {
      type: 'top-zone',
      stageId,
      stageName
    }
  });

  if (!isDragging) return null;

  return (
    <div 
      ref={setNodeRef}
      style={{
        height: '40px',
        backgroundColor: isOver ? '#1976d2' : '#e3f2fd',
        border: isOver ? '2px solid #1976d2' : '2px dashed #90caf9',
        borderRadius: '8px',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
    >
      <span style={{
        color: isOver ? '#fff' : '#1976d2',
        fontSize: '0.875rem',
        fontWeight: 600
      }}>
        {isOver ? 'Drop here' : 'Drop to move here'}
      </span>
    </div>
  );
}

// Droppable Column Component
function DroppableColumn({ stageId, stageName, stage, onFileDrop, children }) {
  const { setNodeRef } = useDroppable({
    id: `droppable-${stageId}`,
    data: {
      type: 'column',
      stageId,
      stageName
    }
  });

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDraggingOver(false);
      }
      return newCount;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      
      // Validate file type
      const videoExtensions = ['.mp4', '.avi', '.mov', '.webm', '.mkv', '.flv', '.wmv', '.m4a'];
      const docExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.csv'];
      
      const isVideo = videoExtensions.some(ext => fileName.endsWith(ext));
      const isDocument = docExtensions.some(ext => fileName.endsWith(ext));
      
      if (isVideo || isDocument) {
        if (onFileDrop) {
          onFileDrop(file, stage);
        }
      } else {
        // Import showErrorToast at component level if not already imported
        if (typeof showErrorToast === 'function') {
          showErrorToast('Please upload only video files (MP4, AVI, MOV, WEBM, MKV, FLV, WMV, M4A) or document files (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, CSV)');
        }
      }
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ 
        transition: 'all 0.2s ease',
        borderRadius: '8px',
        minHeight: '50px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: isDraggingOver ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        border: isDraggingOver ? '2px dashed #1976d2' : '2px dashed transparent',
        position: 'relative'
      }}
    >
      {isDraggingOver && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(25, 118, 210, 0.05)',
          border: '2px dashed #1976d2',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <div style={{
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            Drop file here to upload
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

// Draggable Document Card Component
function DraggableDocumentCard({ document, matches, onDocumentClick, formatDateTime, onDelete, onUpdate, companyId, stageId, onAddToLearning }) {
  const userInfo = UserInfo();
  const canDelete = userInfo?.role?.includes('SUPER_ADMIN') || userInfo?.role?.includes('ADMIN');
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: document.id ? document.id.toString() : `doc-${Date.now()}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const [open, setOpen] = useState(false);
  const [currentViewer, setCurrentViewer] = useState(0);
  const [viewerError, setViewerError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedFileName, setEditedFileName] = useState(document.name || document.title || '');
  // Local state to track the current document name for immediate UI update
  const [currentDocumentName, setCurrentDocumentName] = useState(document.name || document.title || '');
  
  // Learning states
  const [learningDialogOpen, setLearningDialogOpen] = useState(false);
  const [learningNotes, setLearningNotes] = useState('');
  const [isInLearning, setIsInLearning] = useState(false);
  const [learningData, setLearningData] = useState(null);
  const [isSavingLearning, setIsSavingLearning] = useState(false);
  const [checkingLearning, setCheckingLearning] = useState(true);
  
  // Update local state when document prop changes
  useEffect(() => {
    setCurrentDocumentName(document.name || document.title || '');
  }, [document.name, document.title]);
  
  // Check if document is already in learning
  useEffect(() => {
    const checkLearningStatus = async () => {
      if (!document.id) {
        setCheckingLearning(false);
        return;
      }
      try {
        const response = await apiInstance.get(`learning/check/${document.id}/`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });
        if (response.data?.in_learning) {
          setIsInLearning(true);
          setLearningData(response.data.data);
        }
      } catch (error) {
        console.error('Error checking learning status:', error);
      } finally {
        setCheckingLearning(false);
      }
    };
    checkLearningStatus();
  }, [document.id]);
  
  const handleDocumentClick = (e) => {
    if (!isDragging) {
      setOpen(true);
      setCurrentViewer(0);
      setViewerError(false);
      setIframeLoading(true);
    }
  };

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    const fileName = document.name || document.title || '';
    // Remove file extension
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
    setEditedFileName(nameWithoutExt);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(document.id);
    }
    handleMenuClose();
  };

  const handleDownloadClick = async (e) => {
    e.stopPropagation();
    handleMenuClose();
    if (document.file && document.id) {
      try {
        // Log the download to database for traceability
        await apiInstance.post('/api/v1/company-documents/log-download/', {
          document_id: document.id
        }, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });
      } catch (error) {
        console.error('Error logging download:', error);
        // Continue with download even if logging fails
      }
      
      // Build full URL
      const fileUrl = document.file.startsWith('http') 
        ? document.file 
        : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${document.file.startsWith('/') ? '' : '/'}${document.file}`;
      
      // Open in new tab - browser will handle download for document types
      window.open(fileUrl, '_blank');
    }
  };

  // Check if file is a downloadable document type (not audio/video/image)
  const isDownloadableDocument = () => {
    if (!document.file) return false;
    const extension = document.file.split('.').pop().toLowerCase();
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf'];
    return documentExtensions.includes(extension);
  };
  
  // Learning handlers
  const handleAddToLearningClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    setLearningDialogOpen(true);
  };
  
  const handleSaveToLearning = async () => {
    if (!learningNotes.trim()) {
      showErrorToast('Please add notes explaining why this content is important');
      return;
    }
    
    setIsSavingLearning(true);
    try {
      const response = await apiInstance.post('learning/', {
        document_id: document.id,
        company_id: companyId,
        stage_id: stageId,
        notes: learningNotes
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      
      if (response.data?.success) {
        showToast('Content added to Learning successfully', 'success');
        setIsInLearning(true);
        setLearningData(response.data.data);
        setLearningDialogOpen(false);
        setLearningNotes('');
        if (onAddToLearning) {
          onAddToLearning(response.data.data);
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.errors?.document_id?.[0] || 
                       error.response?.data?.message || 
                       'Failed to add content to Learning';
      showErrorToast(errorMsg);
    } finally {
      setIsSavingLearning(false);
    }
  };

  const handleSaveFileName = async () => {
    try {
      const originalName = document.name || document.title || '';
      const newFileName = await updateDocumentFileName(document.id, originalName, editedFileName);

      setCurrentDocumentName(newFileName);

      if (document.name !== undefined) {
        document.name = newFileName;
      }
      if (document.title !== undefined) {
        document.title = newFileName;
      }

      if (onUpdate) {
        onUpdate(document.id, { name: newFileName, title: newFileName });
      }

      showToast('File name updated successfully', 'success');
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating file name:', error);
      showErrorToast('Failed to update file name');
    }
  };

  const getViewers = (fileUrl) => {
    if (!fileUrl) return [];
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`;
    const encodedUrl = encodeURIComponent(fullUrl);
    return [
      { name: 'docMentis Viewer', type: 'component', src: fullUrl },
      { name: 'Google Docs Viewer', type: 'iframe', url: `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true` },
      { name: 'Microsoft Office Viewer', type: 'iframe', url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}` }
    ];
  };

  const viewDialogViewers = getViewers(document.file);

  const tryNextViewer = () => {
    if (currentViewer < viewDialogViewers.length - 1) {
      setCurrentViewer(prev => prev + 1);
      setIframeLoading(true);
    } else {
      setViewerError(true);
    }
  };
  
  const getFileType = () => {
    if (!document.file) return 'document';
    const extension = document.file.split('.').pop().toLowerCase();
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(extension)) return 'audio';
    if (['mp4', 'avi', 'mov', 'webm', 'mkv', '3gp', 'flv'].includes(extension)) return 'video';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return 'image';
    if (['html', 'htm'].includes(extension)) return 'html';
    return 'document';
  };

  const fileType = getFileType();

  const htmlFileUrl = document.file
    ? (document.file.startsWith('http')
        ? document.file
        : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${document.file}`)
    : null;

  if (fileType === 'video') {
    return (
      <DraggableMeetingCard
        meeting={document}
        formatDateTime={formatDateTime}
        onDelete={onDelete}
        companyId={companyId}
        stageId={stageId}
      />
    );
  }

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={handleDocumentClick}
        sx={{ 
          mb: 2, 
          cursor: isDragging ? 'grabbing' : 'grab',
          '&:hover': { 
            borderColor: '#1976d2',
            border: '2px solid #1976d2'
          },
          userSelect: 'none'
        }}
      >
        <Box sx={{ 
          height: 160, 
          backgroundColor: 
            fileType === 'audio' ? '#fff3e0' : 
            fileType === 'video' ? '#000' : 
            fileType === 'image' ? '#f3e5f5' : '#e8f4fd', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'space-between',
          position: 'relative',
          p: 2,
          overflow: 'hidden'
        }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
            {fileType === 'audio' ? (
              <Mic size={48} color="#ff9800" />
            ) : fileType === 'video' ? (
              <>
                <video
                  src={document.file}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                  muted
                  playsInline
                  preload="metadata"
                  crossOrigin="anonymous"
                />
                <Play size={48} color="rgba(255,255,255,0.9)" style={{ position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
              </>
            ) : (
              <FileText size={48} color="#2196f3" />
            )}
          </Box>
          {fileType === 'audio' ? (
            <Chip 
              label="AUDIO" 
              size="small"
              sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                backgroundColor: '#ff9800', 
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }} 
            />
          ) : fileType === 'video' ? (
            <Chip 
              label="VIDEO" 
              size="small"
              sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                backgroundColor: '#f44336', 
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }} 
            />
          ) : null}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: '#e3f2fd' },
              width: 28,
              height: 28
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ textAlign: 'left', pb: 1, px: 1, width: '100%' }}>
            <Tooltip title={currentDocumentName || 'Document'} arrow>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  color: fileType === 'video' ? 'white' : 'inherit',
                  mb: 0.5,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                  maxWidth: '100%'
                }}
              >
                {currentDocumentName || 'Document'}
              </Typography>
            </Tooltip>
            <Typography variant="caption" sx={{ color: fileType === 'video' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)', fontSize: '0.65rem', display: 'block', lineHeight: 1.3 }}>
              {document.created_by ? `${document.created_by.first_name} ${document.created_by.last_name}` : 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: fileType === 'video' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)', fontSize: '0.65rem', display: 'block' }}>
              {getDDMMYYYYFormat(document.uploaded_on || document.created_at)}
            </Typography>
          </Box>
        </Box>
      </Card>
      
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth={fileType === 'audio' ? 'sm' : 'lg'}
        fullWidth={fileType !== 'audio'}
        PaperProps={fileType === 'audio' ? {
          sx: {
            width: '90%',
            maxWidth: '500px',
            maxHeight: 'none',
            height: 'auto'
          }
        } : undefined}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{currentDocumentName || 'Document'}</Typography>
            {fileType === 'document' && viewDialogViewers && viewDialogViewers.length > 1 && (
              <Typography variant="caption" color="text.secondary">
                Viewer: {viewDialogViewers[currentViewer]?.name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: fileType === 'audio' ? 2 : 0, 
          pt: fileType === 'audio' ? 1 : 0,
          height: fileType === 'audio' ? 'auto' : '80vh', 
          position: 'relative',
          ...(fileType === 'audio' && { '&.MuiDialogContent-root': { overflowY: 'visible' } })
        }}>
          {iframeLoading && !viewerError && fileType === 'document' && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading document...
              </Typography>
            </Box>
          )}
          {fileType === 'html' ? (
            // Sandboxed iframe: 'allow-scripts' lets interactive HTML (tabs, calculators)
            // run, but we deliberately omit 'allow-same-origin' so the uploaded markup
            // runs in an opaque origin and cannot reach the app's token/DOM (stored-XSS guard).
            <iframe
              src={htmlFileUrl}
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={currentDocumentName || 'HTML Document'}
            />
          ) : viewerError && fileType === 'document' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <Typography color="text.secondary">
                Unable to preview this file. Please download it to view.
              </Typography>
            </Box>
          ) : fileType === 'document' && viewDialogViewers?.[currentViewer] ? (
            viewDialogViewers[currentViewer].type === 'component' ? (
              <div key={currentViewer} style={{ width: '100%', height: '100%', opacity: iframeLoading ? 0 : 1, transition: 'opacity 0.3s' }}>
                <UDocViewer
                  src={viewDialogViewers[currentViewer].src}
                  onLoad={() => setIframeLoading(false)}
                  onError={tryNextViewer}
                />
              </div>
            ) : (
              <iframe
                key={currentViewer}
                src={viewDialogViewers[currentViewer].url}
                style={{ width: '100%', height: '100%', border: 'none', opacity: iframeLoading ? 0 : 1, transition: 'opacity 0.3s' }}
                title={currentDocumentName || 'Document'}
                onLoad={() => setIframeLoading(false)}
                onError={tryNextViewer}
              />
            )
          ) : (
            <MediaViewer 
              file={{ fileUrl: document.file }} 
              showPlayBackSpeed={fileType === 'audio'}
              matches={matches}
              documentData={fileType === 'audio' ? document : null}
            />
          )}
        </DialogContent>
        <DialogActions>
          {fileType === 'document' && viewDialogViewers && currentViewer < viewDialogViewers.length - 1 && !viewerError && (
            <Button onClick={tryNextViewer} color="warning">
              Try Alternative Viewer
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 3-Dot Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit Name
        </MenuItem>
        {/* Download option - only for documents (pdf, docx, xlsx, ppt, etc.) */}
        {isDownloadableDocument() && (
          <MenuItem onClick={handleDownloadClick}>
            <DownloadIcon sx={{ mr: 1, fontSize: 18, color: '#4caf50' }} />
            <Typography color="#4caf50">Download</Typography>
          </MenuItem>
        )}
        {/* Add to Learning option - only for videos, audio, and documents (NOT images) */}
        {fileType !== 'image' && (
          <Tooltip 
            title={isInLearning ? "This content is already in Learning" : ""} 
            placement="left"
          >
            <span>
              <MenuItem 
                onClick={handleAddToLearningClick}
                disabled={isInLearning || checkingLearning}
                sx={{
                  opacity: isInLearning ? 0.5 : 1,
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  }
                }}
              >
                <SchoolIcon sx={{ mr: 1, fontSize: 18, color: isInLearning ? '#9e9e9e' : '#1976d2' }} />
                <Typography color={isInLearning ? '#9e9e9e' : '#1976d2'}>
                  {isInLearning ? 'Already in Learning' : 'Add to Learning'}
                </Typography>
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {onDelete && canDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
            <Typography color="#d32f2f">Delete</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Edit File Name Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit File Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            value={editedFileName}
            onChange={(e) => setEditedFileName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveFileName} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add to Learning Drawer */}
      <Drawer
        anchor="right"
        open={learningDialogOpen}
        onClose={() => setLearningDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#1976d2', 
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            <Typography variant="h6">Add to Learning</Typography>
          </Box>
          <IconButton onClick={() => setLearningDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Adding</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{currentDocumentName || 'Document'}</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Learning Notes</Typography>
            <TextField
              autoFocus
              multiline
              rows={5}
              fullWidth
              placeholder={"Explain why this content is important for learning. Include:\n• Key takeaways or lessons\n• Important sections or pages\n• Who should review this and why"}
              value={learningNotes}
              onChange={(e) => setLearningNotes(e.target.value)}
              InputProps={{
                sx: { fontSize: '0.9rem' }
              }}
            />
          </Box>

          <Box sx={{ 
            p: 2, 
            bgcolor: '#fff3e0', 
            borderRadius: 1,
            border: '1px solid #ffcc80'
          }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Tip:</strong> Good notes help your team understand why this content matters. 
              Include context about when to use this, key insights, or relevant sections.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setLearningDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleSaveToLearning}
            variant="contained"
            loading={isSavingLearning}
            startIcon={<SchoolIcon />}
          >
            Add to Learning
          </LoadingButton>
        </Box>
      </Drawer>
    </>
  );
}

// Draggable Meeting Card Component
function DraggableMeetingCard({ meeting, formatDateTime, onDelete, companyId, stageId, onAddToLearning }) {
  const userInfo = UserInfo();
  const canDelete = userInfo?.role?.includes('SUPER_ADMIN') || userInfo?.role?.includes('ADMIN');
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: meeting.id ? meeting.id.toString() : `meeting-${Date.now()}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const [open, setOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedFileName, setEditedFileName] = useState(meeting.title || '');
  const [currentMeetingTitle, setCurrentMeetingTitle] = useState(meeting.title || '');
  
  // Learning states (lightweight - drawer handled by shared component)
  const [learningDialogOpen, setLearningDialogOpen] = useState(false);
  const [isInLearning, setIsInLearning] = useState(false);
  const [checkingLearning, setCheckingLearning] = useState(true);
  
  // Share states (lightweight - drawer handled by shared component)
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [checkingShared, setCheckingShared] = useState(true);

  useEffect(() => {
    setCurrentMeetingTitle(meeting.title || '');
  }, [meeting.title]);
  
  // Check if video is already shared
  useEffect(() => {
    const checkSharedStatus = async () => {
      if (!meeting.id) { setCheckingShared(false); return; }
      try {
        const response = await apiInstance.get(`shared-videos/check/${meeting.id}/`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });
        if (response.data?.is_shared) setIsShared(true);
      } catch (error) {
        console.error('Error checking shared status:', error);
      } finally { setCheckingShared(false); }
    };
    checkSharedStatus();
  }, [meeting.id]);
  
  // Check if video is already in learning
  useEffect(() => {
    const checkLearningStatus = async () => {
      if (!meeting.id) { setCheckingLearning(false); return; }
      try {
        const response = await apiInstance.get(`learning/check/${meeting.id}/`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });
        if (response.data?.in_learning) setIsInLearning(true);
      } catch (error) {
        console.error('Error checking learning status:', error);
      } finally { setCheckingLearning(false); }
    };
    checkLearningStatus();
  }, [meeting.id]);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    const fileName = meeting.title || '';
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
    setEditedFileName(nameWithoutExt);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(meeting.id);
    handleMenuClose();
  };

  const handleSaveFileName = async () => {
    try {
      const originalName = meeting.title || '';
      const newFileName = await updateDocumentFileName(meeting.id, originalName, editedFileName);
      setCurrentMeetingTitle(newFileName);
      if (meeting.title !== undefined) meeting.title = newFileName;
      if (meeting.name !== undefined) meeting.name = newFileName;
      showToast('File name updated successfully', 'success');
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating file name:', error);
      showErrorToast('Failed to update file name');
    }
  };
  
  const handleAddToLearningClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    setLearningDialogOpen(true);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    setShareDrawerOpen(true);
  };

  const [thumbnail, setThumbnail] = useState(null);
  useEffect(() => {
    const generate = async () => {      
      try {
        const rawUrl = meeting.file || meeting.recordingUrl || '';
        if (!rawUrl) return;
        const src = rawUrl.startsWith('http') ? rawUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${rawUrl}`;
        const img = await createVideoThumbnail(src);
        if (img) setThumbnail(img);
      } catch (e) {
        console.error('❌ Thumbnail generation failed:', e.message, 'URL:', meeting.file);
      }
    };
    generate();
  }, [meeting]);

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={() => !isDragging && setOpen(true)}
        sx={{ 
          mb: 2, 
          cursor: isDragging ? 'grabbing' : 'grab',
          '&:hover': { 
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          },
          userSelect: 'none',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ 
          height: 160, 
          backgroundColor: '#000',
          backgroundImage: thumbnail ? `url(${thumbnail})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'space-between',
          position: 'relative',
          p: 2,
          overflow: 'hidden'
        }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {thumbnail ? null :
              <video
              src={meeting.file || meeting.recordingUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              muted
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              />
            }
            <Play size={48} color="rgba(255,255,255,0.9)" style={{ position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
          </Box>
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: '#e3f2fd' },
              width: 28,
              height: 28
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Chip 
            label="REC" 
            size="small"
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              backgroundColor: '#f44336', 
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }} 
          />
        </Box>

        <Box sx={{ textAlign: 'left', p: 1, width: '100%' }}>
          <Tooltip title={currentMeetingTitle || 'Meeting Recording'} arrow>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.75rem',
                maxWidth: '100%'
              }}
            >
              {currentMeetingTitle || 'Meeting Recording'}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.8)', fontSize: '0.65rem', display: 'block', lineHeight: 1.3 }}>
            Created By: {meeting.created_by ? `${meeting.created_by.first_name} ${meeting.created_by.last_name}` : 'Unknown'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.7)', fontSize: '0.65rem', display: 'block' }}>
            Date: {getDDMMYYYYFormat(meeting.uploaded_on || meeting.date || meeting.created_at)}
          </Typography>
        </Box>
      </Card>
      
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{currentMeetingTitle || 'Meeting Recording'}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <video
            controls
            style={{ width: '100%', height: 'auto', maxHeight: '500px' }}
            src={meeting.file || meeting.recordingUrl}
          >
            Your browser does not support the video tag.
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 3-Dot Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit Name
        </MenuItem>
        {/* Add to Learning option - only for videos */}
        <Tooltip 
          title={isInLearning ? "This video is already in Learning" : ""} 
          placement="left"
        >
          <span>
            <MenuItem 
              onClick={handleAddToLearningClick}
              disabled={isInLearning || checkingLearning}
              sx={{
                opacity: isInLearning ? 0.5 : 1,
                '&.Mui-disabled': {
                  opacity: 0.5,
                }
              }}
            >
              <SchoolIcon sx={{ mr: 1, fontSize: 18, color: isInLearning ? '#9e9e9e' : '#1976d2' }} />
              <Typography color={isInLearning ? '#9e9e9e' : '#1976d2'}>
                {isInLearning ? 'Already in Learning' : 'Add to Learning'}
              </Typography>
            </MenuItem>
          </span>
        </Tooltip>
        {/* Share Video option */}
        <MenuItem 
          onClick={handleShareClick}
          disabled={checkingShared}
        >
          <Share2 size={18} style={{ marginRight: 8, color: '#2e7d32' }} />
          <Typography color="#2e7d32">
            Share Video
          </Typography>
        </MenuItem>
        {onDelete && canDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
            <Typography color="#d32f2f">Delete</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Edit File Name Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit File Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            value={editedFileName}
            onChange={(e) => setEditedFileName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveFileName} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add to Learning Drawer - uses shared component */}
      <AddToLearningDrawer
        open={learningDialogOpen}
        onClose={() => setLearningDialogOpen(false)}
        documentId={meeting.id}
        documentTitle={currentMeetingTitle}
        companyId={companyId}
        stageId={stageId}
        onSuccess={(data) => { setIsInLearning(true); if (onAddToLearning) onAddToLearning(data); }}
      />

      {/* Share Video Drawer - uses shared component */}
      <ShareVideoDrawer
        open={shareDrawerOpen}
        onClose={() => setShareDrawerOpen(false)}
        documentId={meeting.id}
        documentTitle={currentMeetingTitle}
        companyId={companyId}
        onShareSuccess={(data) => setIsShared(true)}
        onRemoveSuccess={() => setIsShared(false)}
      />
    </>
  );
}

// Draggable Audio Card Component
function DraggableAudioCard({ audio, formatDateTime, onDelete }) {
  const userInfo = UserInfo();
  const canDelete = userInfo?.role?.includes('SUPER_ADMIN') || userInfo?.role?.includes('ADMIN');
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: audio.id ? audio.id.toString() : `audio-${Date.now()}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const [open, setOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedFileName, setEditedFileName] = useState(audio.title || '');
  const [currentAudioTitle, setCurrentAudioTitle] = useState(audio.title || '');
  const fileUrl = audio?.file || audio?.audioUrl || '';
  const audioSrc = fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`;

  useEffect(() => {
    setCurrentAudioTitle(audio.title || '');
  }, [audio.title]);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    const fileName = audio.title || '';
    // Remove file extension
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
    setEditedFileName(nameWithoutExt);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(audio.id);
    }
    handleMenuClose();
  };

  const handleSaveFileName = async () => {
    try {
      const originalName = audio.title || '';
      const newFileName = await updateDocumentFileName(audio.id, originalName, editedFileName);

      setCurrentAudioTitle(newFileName);

      if (audio.title !== undefined) {
        audio.title = newFileName;
      }
      if (audio.name !== undefined) {
        audio.name = newFileName;
      }

      showToast('File name updated successfully', 'success');
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating file name:', error);
      showErrorToast('Failed to update file name');
    }
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={() => !isDragging && setOpen(true)}
        sx={{ 
          mb: 2, 
          cursor: isDragging ? 'grabbing' : 'grab',
          '&:hover': { 
            borderColor: '#1976d2',
            border: '2px solid #1976d2'
          },
          userSelect: 'none'
        }}
      >
        <Box sx={{
          height: 160,
          backgroundColor: '#fff3e0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          p: 2
        }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Mic size={48} color="#ff9800" />
          </Box>
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: '#e3f2fd' },
              width: 28,
              height: 28
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Chip
            label="AUDIO"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: '#ff9800',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          />
          <Box sx={{ textAlign: 'left', pb: 1, px: 1, width: '100%' }}>
            <Tooltip title={currentAudioTitle || 'Audio Recording'} arrow>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 0.5,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                  maxWidth: '100%'
                }}
              >
                {currentAudioTitle || 'Audio Recording'}
              </Typography>
            </Tooltip>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.65rem', display: 'block', lineHeight: 1.3 }}>
              {audio.created_by ? `${audio.created_by.first_name} ${audio.created_by.last_name}` : 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.65rem', display: 'block' }}>
              {getDDMMYYYYFormat(audio.uploaded_on || audio.created_at)}
            </Typography>
          </Box>
        </Box>
      </Card>

      <Dialog 
        open={open} 
        onClose={() => {
          setOpen(false);
        }} 
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, flexShrink: 0 }}>
          <Typography variant="h6">{currentAudioTitle || 'Audio Recording'}</Typography>
          <IconButton onClick={() => {
            setOpen(false);
          }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 2, 
          pt: 1, 
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0
        }}>
          <MediaViewer 
            file={{ fileUrl: fileUrl }} 
            showPlayBackSpeed={true}
            matches={false}
            documentData={audio}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, flexShrink: 0 }}>
          <Button onClick={() => {
            setOpen(false);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 3-Dot Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit Name
        </MenuItem>
        {onDelete && canDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
            <Typography color="#d32f2f">Delete</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Edit File Name Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit File Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            value={editedFileName}
            onChange={(e) => setEditedFileName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveFileName} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Scheduled Meeting Card Component (Non-draggable)
function DraggableScheduledMeetingCard({ scheduledMeeting, formatDateTime, matches, companyId, stageId, onMeetingCancelled }) {
  const userInfo = UserInfo();
  const canDeleteRecording = userInfo?.role?.includes('SUPER_ADMIN') || userInfo?.role?.includes('ADMIN');
  
  const [open, setOpen] = useState(false);
  const [recordingLinks, setRecordingLinks] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [fetchingTeamsRecording, setFetchingTeamsRecording] = useState(false);
  const [teamsRecordingDoc, setTeamsRecordingDoc] = useState(scheduledMeeting.recording_document);
  const hasFetched = useRef(false);
  const hasTriedTeamsFetch = useRef(false);
  
  // Recording menu states (for card thumbnail 3-dot menu)
  const [recMenuAnchorEl, setRecMenuAnchorEl] = useState(null);
  const [recEditDialogOpen, setRecEditDialogOpen] = useState(false);
  const [recEditedFileName, setRecEditedFileName] = useState('');
  const [recDeleteDialogOpen, setRecDeleteDialogOpen] = useState(false);
  const [recIsDeleting, setRecIsDeleting] = useState(false);
  const [recordingDeleted, setRecordingDeleted] = useState(false);
  
  // Learning states (lightweight - drawer handled by shared component)
  const [recLearningDrawerOpen, setRecLearningDrawerOpen] = useState(false);
  const [recIsInLearning, setRecIsInLearning] = useState(false);
  const [recCheckingLearning, setRecCheckingLearning] = useState(true);
  
  // Share states (lightweight - drawer handled by shared component)
  const [recShareDrawerOpen, setRecShareDrawerOpen] = useState(false);
  const [recIsShared, setRecIsShared] = useState(false);
  const [recCheckingShared, setRecCheckingShared] = useState(true);
  
  const handleRecMenuClick = (e) => {
    e.stopPropagation();
    setRecMenuAnchorEl(e.currentTarget);
  };
  
  const handleRecMenuClose = (e) => {
    if (e) e.stopPropagation();
    setRecMenuAnchorEl(null);
  };

  // Check if recording is already shared
  useEffect(() => {
    const checkSharedStatus = async () => {
      const activeDoc = recordingDeleted ? null : (teamsRecordingDoc || scheduledMeeting.recording_document);
      if (!activeDoc?.id) { setRecCheckingShared(false); return; }
      try {
        const response = await apiInstance.get(`shared-videos/check/${activeDoc.id}/`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });
        if (response.data?.is_shared) setRecIsShared(true);
      } catch (error) {
        console.error('Error checking shared status:', error);
      } finally { setRecCheckingShared(false); }
    };
    checkSharedStatus();
  }, [teamsRecordingDoc, scheduledMeeting.recording_document, recordingDeleted]);

  // Check if recording is already in learning
  useEffect(() => {
    const checkLearningStatus = async () => {
      const activeDoc = recordingDeleted ? null : (teamsRecordingDoc || scheduledMeeting.recording_document);
      if (!activeDoc?.id) { setRecCheckingLearning(false); return; }
      try {
        const response = await apiInstance.get(`learning/check/${activeDoc.id}/`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });
        if (response.data?.in_learning) setRecIsInLearning(true);
      } catch (error) {
        console.error('Error checking learning status:', error);
      } finally { setRecCheckingLearning(false); }
    };
    checkLearningStatus();
  }, [teamsRecordingDoc, scheduledMeeting.recording_document, recordingDeleted]);

  const handleRecEditClick = (e) => {
    e.stopPropagation();
    const activeDoc = teamsRecordingDoc || scheduledMeeting.recording_document;
    const fileName = activeDoc?.file_name || '';
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
    setRecEditedFileName(nameWithoutExt);
    setRecEditDialogOpen(true);
    handleRecMenuClose();
  };

  const handleRecDeleteClick = (e) => {
    e.stopPropagation();
    handleRecMenuClose();
    setRecDeleteDialogOpen(true);
  };

  const handleRecSaveFileName = async () => {
    const activeDoc = teamsRecordingDoc || scheduledMeeting.recording_document;
    if (!activeDoc?.id || !recEditedFileName.trim()) return;
    try {
      const originalName = activeDoc.file_name || '';
      const newFileName = await updateDocumentFileName(activeDoc.id, originalName, recEditedFileName);
      if (teamsRecordingDoc) {
        setTeamsRecordingDoc({ ...teamsRecordingDoc, file_name: newFileName });
      }
      showToast('File name updated successfully', 'success');
      setRecEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating file name:', error);
      showErrorToast('Failed to update file name');
    }
  };
  
  const handleRecLearningClick = (e) => {
    e.stopPropagation();
    handleRecMenuClose();
    setRecLearningDrawerOpen(true);
  };

  const handleRecShareClick = (e) => {
    e.stopPropagation();
    handleRecMenuClose();
    setRecShareDrawerOpen(true);
  };

  const handleRecConfirmDelete = async () => {
    const activeDoc = teamsRecordingDoc || scheduledMeeting.recording_document;
    if (!activeDoc?.id) return;
    setRecIsDeleting(true);
    try {
      await apiInstance.delete(`documents/${activeDoc.id}/`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      showToast('Recording deleted', 'success');
      setRecDeleteDialogOpen(false);
      setTeamsRecordingDoc(null);
      setRecordingDeleted(true);
    } catch (error) {
      showErrorToast('Failed to delete recording');
    } finally {
      setRecIsDeleting(false);
    }
  };

  // Generate thumbnail from recording file when available
  useEffect(() => {
    const generate = async () => {
      try {
        const rawUrl = teamsRecordingDoc?.file || scheduledMeeting.recording_document?.file || '';
        if (!rawUrl) return;
        const src = rawUrl.startsWith('http') ? rawUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${rawUrl}`;
        const img = await createVideoThumbnail(src);
        if (img) setThumbnail(img);
      } catch (e) {
        // thumbnail generation failed, use fallback
      }
    };
    generate();
  }, [teamsRecordingDoc, scheduledMeeting.recording_document]);

  // On-demand fetch for Teams recordings when dialog opens
  // This ensures old recordings that weren't fetched by Celery are retrieved when viewed
  useEffect(() => {
    const fetchTeamsRecording = async () => {
      // Only for Teams meetings without recording, meeting has ended, and haven't tried yet
      const meetingEnded = scheduledMeeting.end_date && new Date(scheduledMeeting.end_date) < new Date();
      const needsFetch = scheduledMeeting.is_microsoft_teams && 
                         !scheduledMeeting.recording_document && 
                         !teamsRecordingDoc &&
                         meetingEnded &&
                         !hasTriedTeamsFetch.current;
      
      if (open && scheduledMeeting?.id && needsFetch) {
        hasTriedTeamsFetch.current = true;
        setFetchingTeamsRecording(true);
        try {
          const response = await apiInstance.post(
            `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/teams-recording-fetch/`,
            { meeting_id: scheduledMeeting.id },
            { headers: { 'Authorization': `Token ${localStorage.getItem('authToken')}` } }
          );
          if (response.status === 200 && response.data?.success && response.data?.recording) {
            setTeamsRecordingDoc(response.data.recording);
          }
        } catch (error) {
          console.error('Error fetching Teams recording:', error);
        } finally {
          setFetchingTeamsRecording(false);
        }
      }
    };
    fetchTeamsRecording();
  }, [open, scheduledMeeting]);

  // Fetch recordings when dialog opens (for BigBlueButton/Gateway Meet)
  useEffect(() => {
    const fetchRecordings = async () => {
      if (open && scheduledMeeting?.id && !hasFetched.current) {
        hasFetched.current = true;
        setLoadingRecordings(true);
        try {
          const response = await apiInstance.post(
            `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/interview-recording/?id=${scheduledMeeting.id}`,
            scheduledMeeting,
            { headers: { 'Authorization': `Token ${localStorage.getItem('authToken')}` } }
          );
          if (response.status === 200) {
            setRecordingLinks(response?.data?.data || []);
          }
        } catch (error) {
          console.error('Error fetching recordings:', error);
        } finally {
          setLoadingRecordings(false);
        }
      }
    };
    fetchRecordings();
  }, [open, scheduledMeeting]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return '#4caf50';
      case 'PENDING':
        return '#ff9800';
      case 'CANCELLED':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Scheduled';
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  // Handle meeting cancellation
  const handleCancelMeeting = async () => {
    setCancelling(true);
    try {
      await apiInstance.post(`${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/meeting/cancel/`, {
        id: scheduledMeeting.id,
        company_id: companyId
      }, {
        headers: { 'Authorization': `Token ${localStorage.getItem('authToken')}` }
      });
      
      showToast('Meeting cancelled successfully', 'success');
      setCancelDialogOpen(false);
      setOpen(false);
      
      // Callback to refresh meeting list
      if (onMeetingCancelled) {
        onMeetingCancelled();
      }
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      showErrorToast(error?.response?.data?.message || 'Failed to cancel meeting');
    } finally {
      setCancelling(false);
    }
  };

  // Use teamsRecordingDoc (from on-demand fetch) or original recording_document
  // If recording was deleted, return null
  const activeRecordingDoc = recordingDeleted ? null : (teamsRecordingDoc || scheduledMeeting.recording_document);
  const hasRecording = !!activeRecordingDoc;
  const recFileUrl = hasRecording && activeRecordingDoc.file
    ? (activeRecordingDoc.file.startsWith('http')
        ? activeRecordingDoc.file
        : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${activeRecordingDoc.file}`)
    : null;

  return (
    <>
      <Card
        onClick={() => setOpen(true)}
        sx={{ 
          mb: 2, 
          cursor: 'pointer',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
          userSelect: 'none',
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #e0e0e0'
        }}
      >
        {/* Video thumbnail area - shown when recording available, otherwise calendar header */}
        {hasRecording && recFileUrl ? (
          <Box sx={{
            height: 160,
            backgroundColor: '#000',
            backgroundImage: thumbnail ? `url(${thumbnail})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            {!thumbnail && (
              <video
                src={recFileUrl}
                style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                muted playsInline preload="metadata" crossOrigin="anonymous"
              />
            )}
            {/* 3-dot menu button */}
            <IconButton 
              size="small" 
              onClick={handleRecMenuClick}
              sx={{ 
                position: 'absolute', 
                top: 8, 
                left: 8, 
                zIndex: 3, 
                backgroundColor: 'rgba(0,0,0,0.6)', 
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Play size={48} color="rgba(255,255,255,0.9)" style={{ position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
            <Chip label="REC" size="small" sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: '#f44336', color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }} />
            {/* Status badge - moved below 3-dot menu */}
            <Chip
              label={getStatusLabel(scheduledMeeting.status)}
              size="small"
              sx={{ position: 'absolute', top: 40, left: 8, backgroundColor: getStatusColor(scheduledMeeting.status), color: 'white', fontSize: '0.65rem', fontWeight: 'bold', height: 20, '& .MuiChip-label': { px: 1 } }}
            />
          </Box>
        ) : (
          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, position: 'relative' }}>
            {/* Status Chip */}
            <Chip
              label={getStatusLabel(scheduledMeeting.status)}
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: getStatusColor(scheduledMeeting.status), color: 'white', fontSize: '0.65rem', fontWeight: 'bold', height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
            />
            {/* Join Button */}
            {scheduledMeeting.microsoft_teams_url && (
              <Tooltip title="Join Microsoft Teams Meeting" arrow>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(scheduledMeeting.microsoft_teams_url, '_blank'); }}
                  sx={{ position: 'absolute', top: 32, right: 8, backgroundColor: '#5059c9', color: 'white', width: 24, height: 24, '&:hover': { backgroundColor: '#4045a8' } }}>
                  <VideoCallIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 9 }}>
              <CalendarToday sx={{ fontSize: 28, color: scheduledMeeting.is_microsoft_teams ? '#5059c9' : '#1976d2', mt: 0.3 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                  {scheduledMeeting.title || 'Scheduled Meeting'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem' }}>
                  {getDDMMYYYYFormat(scheduledMeeting.start_date)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Info section below thumbnail/header - always shown */}
        <Box sx={{ p: 1.5 }}>
          {/* Recording filename + date when recording available */}
          {hasRecording && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }} noWrap>
                {activeRecordingDoc.file_name || 'Meeting Recording'}
              </Typography>
              {scheduledMeeting.created_by && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }} noWrap>
                  Created By: {scheduledMeeting.created_by.first_name} {scheduledMeeting.created_by.last_name}
                </Typography>
              )}
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                Date: {getDDMMYYYYFormat(scheduledMeeting.start_date)}
              </Typography>
              <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1 }} />
            </>
          )}

          {/* Attendees */}
          {scheduledMeeting.attendees_data && scheduledMeeting.attendees_data.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
              <Users size={12} color="#64748b" />
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem', fontWeight: 600 }}>Attendees:</Typography>
              {scheduledMeeting.attendees_data.slice(0, 3).map((att, index) => (
                <Chip key={index} label={att.name || att.email} size="small"
                  sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#e3f2fd', color: '#1976d2', '& .MuiChip-label': { px: 1 } }} />
              ))}
              {scheduledMeeting.attendees_data.length > 3 && (
                <Chip label={`+${scheduledMeeting.attendees_data.length - 3} more`} size="small"
                  sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#f5f5f5', color: '#64748b', '& .MuiChip-label': { px: 1 } }} />
              )}
            </Box>
          )}

          {/* Moderators */}
          {scheduledMeeting.moderators && scheduledMeeting.moderators.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
              <Users size={12} color="#64748b" />
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem', fontWeight: 600 }}>Moderators:</Typography>
              {scheduledMeeting.moderators.slice(0, 2).map((moderator, index) => (
                <Chip key={index} label={`${moderator.first_name} ${moderator.last_name}`} size="small"
                  sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#fff3e0', color: '#f57c00', '& .MuiChip-label': { px: 1 } }} />
              ))}
              {scheduledMeeting.moderators.length > 2 && (
                <Chip label={`+${scheduledMeeting.moderators.length - 2} more`} size="small"
                  sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#f5f5f5', color: '#64748b', '& .MuiChip-label': { px: 1 } }} />
              )}
            </Box>
          )}

          {/* Cancel Meeting Button */}
          {scheduledMeeting.status === 'CONFIRMED' && (
            <Box sx={{ pt: 0.75, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
              <Button size="small"
                onClick={(e) => { e.stopPropagation(); setCancelDialogOpen(true); }}
                sx={{ color: '#f44336', fontSize: '0.7rem', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' } }}
                startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
              >
                Cancel Meeting
              </Button>
            </Box>
          )}
        </Box>
      </Card>

      {/* Meeting Details Dialog */}
      <Dialog 
        open={open} 
        onClose={() => {
          setOpen(false);
          // Reset fetch flag when dialog closes so recordings can be fetched again if reopened
          hasFetched.current = false;
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ color: '#1976d2' }} />
              <Typography variant="h6">{scheduledMeeting.title || 'Scheduled Meeting'}</Typography>
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          {/* Microsoft Teams Join Button */}
          {scheduledMeeting.microsoft_teams_url && (() => {
            const isMeetingOver = scheduledMeeting.end_date && new Date(scheduledMeeting.end_date) < new Date();
            return (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f0f4ff', borderRadius: 1, border: '1px solid #5059c9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VideoCallIcon sx={{ color: '#5059c9' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#5059c9', flex: 1 }}>
                    Microsoft Teams Meeting
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VideoCallIcon />}
                    onClick={() => window.open(scheduledMeeting.microsoft_teams_url, '_blank')}
                    disabled={isMeetingOver}
                    sx={{ 
                      backgroundColor: '#5059c9', 
                      '&:hover': { backgroundColor: '#4045a8' },
                      '&.Mui-disabled': { backgroundColor: '#c5c7e8', color: '#fff' },
                      textTransform: 'none'
                    }}
                  >
                    {isMeetingOver ? 'Meeting Ended' : 'Join Meeting'}
                  </Button>
                </Box>
              </Box>
            );
          })()}

          {/* Recording Section - Show when recording is available */}
          {activeRecordingDoc && (
            <TeamsRecordingCard 
              recording={activeRecordingDoc} 
              meetingId={scheduledMeeting.id}
              onRecordingFetched={(newRecording) => setTeamsRecordingDoc(newRecording)}
              companyId={companyId}
              stageId={stageId}
            />
          )}

          {/* Recording Status - Show when fetching or pending */}
          {scheduledMeeting.is_microsoft_teams && !activeRecordingDoc && (fetchingTeamsRecording || scheduledMeeting.recording_status) && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: scheduledMeeting.recording_status === 'FAILED' ? '#fff5f5' : '#f5f5f5', borderRadius: 1, border: scheduledMeeting.recording_status === 'FAILED' ? '1px solid #ffcdd2' : 'none' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Videocam sx={{ fontSize: 14 }} />
                Recording: {
                  fetchingTeamsRecording ? 'Fetching recording from Microsoft...' :
                  scheduledMeeting.recording_status === 'PENDING' ? 'Waiting for recording...' :
                  scheduledMeeting.recording_status === 'FETCHING' ? 'Fetching recording...' :
                  scheduledMeeting.recording_status === 'NOT_FOUND' ? 'No recording found' :
                  scheduledMeeting.recording_status === 'FAILED' ? 'Failed to fetch recording' :
                  scheduledMeeting.recording_status
                }
              </Typography>
              {/* Retry button for FAILED/NOT_FOUND statuses */}
              {!fetchingTeamsRecording && ['FAILED', 'NOT_FOUND'].includes(scheduledMeeting.recording_status) && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    setFetchingTeamsRecording(true);
                    try {
                      const response = await apiInstance.post(
                        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/teams-recording-fetch/`,
                        { meeting_id: scheduledMeeting.id, force_refetch: false },
                        { headers: { 'Authorization': `Token ${localStorage.getItem('authToken')}` } }
                      );
                      if (response.status === 200 && response.data?.success && response.data?.recording) {
                        setTeamsRecordingDoc(response.data.recording);
                        showToast('Recording fetched successfully!', 'success');
                      } else {
                        showErrorToast(response.data?.message || 'Recording not available yet. Please try again later.');
                      }
                    } catch (error) {
                      showErrorToast(error.response?.data?.message || 'Failed to fetch recording');
                    } finally {
                      setFetchingTeamsRecording(false);
                    }
                  }}
                  disabled={fetchingTeamsRecording}
                  sx={{ mt: 1, bgcolor: '#1976d2', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#1565c0' } }}
                  startIcon={fetchingTeamsRecording ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <RefreshCw size={14} />}
                >
                  {fetchingTeamsRecording ? 'Fetching...' : 'Retry Fetch Recording'}
                </Button>
              )}
            </Box>
          )}

          {/* Meeting Time & Status - Compact inline layout */}
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: '250px' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Meeting Time
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                <strong>Start:</strong> {formatDateTime(scheduledMeeting.start_date, scheduledMeeting.meeting_timezone)}
              </Typography>
              {scheduledMeeting.end_date && (
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  <strong>End:</strong> {formatDateTime(scheduledMeeting.end_date, scheduledMeeting.meeting_timezone)}
                </Typography>
              )}
            </Box>
            <Box>
              <Chip 
                label={getStatusLabel(scheduledMeeting.status)} 
                size="small"
                sx={{ 
                  backgroundColor: getStatusColor(scheduledMeeting.status), 
                  color: 'white',
                  fontWeight: 'bold',
                  height: 24
                }} 
              />
            </Box>
          </Box>

          {/* Attendees - Compact inline */}
          {scheduledMeeting.attendees_data && scheduledMeeting.attendees_data.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Attendees ({scheduledMeeting.attendees_data.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {scheduledMeeting.attendees_data.map((att, index) => (
                  <Chip 
                    key={index} 
                    label={att.name || att.email} 
                    size="small" 
                    sx={{ height: 24, fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Moderators - Compact inline */}
          {scheduledMeeting.moderators && scheduledMeeting.moderators.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Moderators
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {scheduledMeeting.moderators.map((moderator, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                      {moderator.first_name?.[0]}{moderator.last_name?.[0]}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {moderator.first_name} {moderator.last_name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Meeting Links - Compact inline */}
          {(scheduledMeeting.meeting_moderator_link || scheduledMeeting.meeting_attendee_link) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Meeting Links
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {scheduledMeeting.meeting_moderator_link && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    href={scheduledMeeting.meeting_moderator_link}
                    target="_blank"
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                  >
                    Moderator Link
                  </Button>
                )}
                {scheduledMeeting.meeting_attendee_link && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    href={scheduledMeeting.meeting_attendee_link}
                    target="_blank"
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                  >
                    Attendee Link
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Recordings - Compact */}
          {recordingLinks && recordingLinks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Recordings ({recordingLinks.length})
              </Typography>
              {loadingRecordings ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>Loading...</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: "35vh" }}>
                  {recordingLinks.map((recording, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                        maxHeight: '100vh',
                        '& > div': {
                          height: '300px !important',
                          overflow: 'hidden !important'
                        },
                        '& video': {
                          height: '100vh',
                          width: '100%',
                          objectFit: 'contain',
                          display: 'block'
                        },
                        '& .multiple-section-meeting': {
                          height: '100vh !important',
                          overflow: 'hidden !important'
                        }
                      }}
                    >
                      <MediaViewer file={recording} isRecording={true} matches={matches} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Comments - Compact */}
          {scheduledMeeting.comments && scheduledMeeting.comments.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Comments ({scheduledMeeting.comments.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {scheduledMeeting.comments.map((comment, index) => (
                  <Paper key={index} sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                        {comment.created_by?.first_name?.[0]}{comment.created_by?.last_name?.[0]}
                      </Avatar>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {comment.created_by?.first_name} {comment.created_by?.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {formatDateTime(comment.created)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{comment.comment_text}</Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Created By */}
          {scheduledMeeting.created_by && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary">
                Created by {scheduledMeeting.created_by.first_name} {scheduledMeeting.created_by.last_name} on {formatDateTime(scheduledMeeting.created_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {scheduledMeeting.status === 'CONFIRMED' && (() => {
            const isMeetingOver = scheduledMeeting.end_date && new Date(scheduledMeeting.end_date) < new Date();
            return (
              <Button
                onClick={() => setCancelDialogOpen(true)}
                color="error"
                variant="outlined"
                disabled={isMeetingOver}
              >
                Cancel Meeting
              </Button>
            );
          })()}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Meeting</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel this meeting?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Meeting:</strong> {scheduledMeeting.title || 'Scheduled Meeting'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Date:</strong> {formatDateTime(scheduledMeeting.start_date, scheduledMeeting.meeting_timezone)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All attendees and moderators will be notified of the cancellation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={cancelling}
          >
            No, Keep Meeting
          </Button>
          <LoadingButton 
            onClick={handleCancelMeeting}
            loading={cancelling}
            color="error"
            variant="contained"
          >
            Yes, Cancel Meeting
          </LoadingButton>
        </DialogActions>
      </Dialog>
      
      {/* Recording 3-dot Menu */}
      <Menu
        anchorEl={recMenuAnchorEl}
        open={Boolean(recMenuAnchorEl)}
        onClose={handleRecMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleRecEditClick}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit Name
        </MenuItem>
        {/* Add to Learning option */}
        <Tooltip 
          title={recIsInLearning ? "This video is already in Learning" : ""} 
          placement="left"
        >
          <span>
            <MenuItem 
              onClick={handleRecLearningClick}
              disabled={recIsInLearning || recCheckingLearning}
              sx={{
                opacity: recIsInLearning ? 0.5 : 1,
                '&.Mui-disabled': {
                  opacity: 0.5,
                }
              }}
            >
              <SchoolIcon sx={{ mr: 1, fontSize: 18, color: recIsInLearning ? '#9e9e9e' : '#1976d2' }} />
              <Typography color={recIsInLearning ? '#9e9e9e' : '#1976d2'}>
                {recIsInLearning ? 'Already in Learning' : 'Add to Learning'}
              </Typography>
            </MenuItem>
          </span>
        </Tooltip>
        {/* Share Video option */}
        <MenuItem 
          onClick={handleRecShareClick}
          disabled={recCheckingShared}
        >
          <Share2 size={18} style={{ marginRight: 8, color: '#2e7d32' }} />
          <Typography color="#2e7d32">
            Share Video
          </Typography>
        </MenuItem>
        {canDeleteRecording && (
          <MenuItem onClick={handleRecDeleteClick}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
            <Typography color="#d32f2f">Delete</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Edit File Name Dialog */}
      <Dialog 
        open={recEditDialogOpen} 
        onClose={() => setRecEditDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit File Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            value={recEditedFileName}
            onChange={(e) => setRecEditedFileName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRecSaveFileName} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add to Learning Drawer - uses shared component */}
      <AddToLearningDrawer
        open={recLearningDrawerOpen}
        onClose={() => setRecLearningDrawerOpen(false)}
        documentId={activeRecordingDoc?.id}
        documentTitle={activeRecordingDoc?.file_name || 'Teams Recording'}
        companyId={companyId}
        stageId={stageId}
        onSuccess={(data) => setRecIsInLearning(true)}
      />

      {/* Share Video Drawer - uses shared component */}
      <ShareVideoDrawer
        open={recShareDrawerOpen}
        onClose={() => setRecShareDrawerOpen(false)}
        documentId={activeRecordingDoc?.id}
        documentTitle={activeRecordingDoc?.file_name || 'Teams Recording'}
        companyId={companyId}
        onShareSuccess={(data) => setRecIsShared(true)}
        onRemoveSuccess={() => setRecIsShared(false)}
      />
      
      {/* Recording Delete Confirmation Dialog */}
      <Dialog open={recDeleteDialogOpen} onClose={() => setRecDeleteDialogOpen(false)} onClick={(e) => e.stopPropagation()} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Recording</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this recording?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>File:</strong> {activeRecordingDoc?.file_name || 'Teams Recording'}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecDeleteDialogOpen(false)} disabled={recIsDeleting}>Cancel</Button>
          <LoadingButton onClick={handleRecConfirmDelete} loading={recIsDeleting} color="error" variant="contained">
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Simple Drag and Drop Wrapper for List Views
function DragDropWrapper({ stage, onFileDrop, children }) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDraggingOver(false);
      }
      return newCount;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      
      // Validate file type
      const videoExtensions = ['.mp4', '.avi', '.mov', '.webm', '.mkv', '.flv', '.wmv', '.m4a'];
      const docExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.csv'];
      
      const isVideo = videoExtensions.some(ext => fileName.endsWith(ext));
      const isDocument = docExtensions.some(ext => fileName.endsWith(ext));
      
      if (isVideo || isDocument) {
        if (onFileDrop) {
          onFileDrop(file, stage);
        }
      } else {
        if (typeof showErrorToast === 'function') {
          showErrorToast('Please upload only video files (MP4, AVI, MOV, WEBM, MKV, FLV, WMV, M4A) or document files (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, CSV)');
        }
      }
    }
  };

  return (
    <div 
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ 
        transition: 'all 0.2s ease',
        borderRadius: '8px',
        position: 'relative',
        backgroundColor: isDraggingOver ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        border: isDraggingOver ? '2px dashed #1976d2' : '2px dashed transparent',
        padding: isDraggingOver ? '8px' : '0'
      }}
    >
      {isDraggingOver && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '2px dashed #1976d2',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          pointerEvents: 'none'
        }}>
          <div style={{
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            Drop file here to upload
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export { DroppableColumn, TopDropZone, DraggableDocumentCard, DraggableMeetingCard, DraggableAudioCard, DraggableScheduledMeetingCard, DragDropWrapper };
